import MessageSender from 'components/MessageSender/MessageSender'
import React, { useContext, useState } from 'react'
import Button from 'components/generic/Button/Button'
import Text from 'components/base/Text'
import { ViewerContext } from 'contexts/ViewerContext'
import styled from 'styled-components'
import css from '@styled-system/css'
import { Box } from '@careguide/theme'
import { Overlay } from '@careguide/overlay'
import Modal from 'components/generic/Modal'
import useBreakpoint from 'hooks/useBreakpoint'
import { MessageSendSuccessfulBox } from './MessageSendSuccessfulBox'
import ContentBlock from 'components/ContentBlock'
import Stack from '@careguide/stack'
import { Heading } from '@careguide/typography'
import { CreditsBanner as CreditsBannerData, CreditsBannerVariables } from 'graph/CreditsBanner'
import CREDITS_USED from 'components/LimitedApplications/query.CreditsBanner.gql'
import { CreditsBanner, getLimitedApplicationsConfig } from 'components/LimitedApplications'
import { useQuery, useApolloClient } from '@apollo/client'

type MessengerProps = {
  isFreeUser: boolean
  limitedApplicationsVariant?: string
  hasConversationWithUser: boolean
  recipient: {
    fullName: string
    firstName: string
  }
  listingId: string
  conversationPath: string
  telesafeUrl: string
  defaultConversationMessage: string
  avatar: {
    largeUrl?: string
    mediumUrl?: string
  }
  similarListings: Array<TODO>
  state: TODO
  city: TODO
}

const Messenger = ({
  isFreeUser,
  limitedApplicationsVariant,
  hasConversationWithUser,
  listingId,
  conversationPath,
  telesafeUrl,
  recipient,
  defaultConversationMessage,
  avatar,
  similarListings = [],
  city,
  state,
}: MessengerProps) => {
  const apollo = useApolloClient()
  const { period, credits } = getLimitedApplicationsConfig(limitedApplicationsVariant)
  const creditsQuery = useQuery<CreditsBannerData, CreditsBannerVariables>(CREDITS_USED, {
    fetchPolicy: 'network-only',
    skip: !limitedApplicationsVariant,
    variables: { period },
  })

  const [showSuccessfulMessage, setShowSuccessfulMessage] = useState(false)
  const [showOutOfCreditsModal, setShowOutOfCreditsModal] = useState(false)
  const [showMobileMessageSender, setShowMobileMessageSender] = useState(false)
  const [hasConversations, setHasConversations] = useState(hasConversationWithUser)
  const isTabletAndUp = useBreakpoint('tablet')
  const viewer = useContext(ViewerContext)
  const hasTelesafe = viewer.hasTelesafe
  const { fullName: fullName, firstName: firstName } = recipient
  const heading = `Send a Message to ${isTabletAndUp ? fullName : firstName}`
  const canSendMessage = !hasConversations && !showSuccessfulMessage
  const userMustHaveCredits = limitedApplicationsVariant && isFreeUser && !hasConversations

  const onMessageSend = (isSuccessful: boolean) => {
    setShowSuccessfulMessage(isSuccessful)
    setHasConversations(isSuccessful)
  }

  const outOfCredits = userMustHaveCredits && credits - creditsQuery.data?.user?.creditsUsed < 1

  /**
   * NOTE: This should technically be backend logic, but since it's still experimenta,
   * it's happening in the frontend. If this experiment is chosen then this logic
   * should be moved to the backend.
   */
  const checkCanSendMessage = async () => {
    if (!userMustHaveCredits) return true

    const { data } = await apollo.query({
      query: CREDITS_USED,
      variables: { period },
      fetchPolicy: 'network-only',
    })

    const hasCredits = credits - data?.user?.creditsUsed > 0

    if (!hasCredits) {
      setShowOutOfCreditsModal(true)
      setShowMobileMessageSender(false)
    }

    return hasCredits
  }

  return (
    <>
      {showOutOfCreditsModal && (
        <Overlay>
          <ContentBlock p='4' maxWidth='600px'>
            <CreditsBanner
              credits={credits}
              period={period}
              isProvider
              onClose={() => setShowOutOfCreditsModal(false)}
            />
          </ContentBlock>
        </Overlay>
      )}
      {isTabletAndUp && !outOfCredits && (
        <ContentBlock pt='5' p='4'>
          <Stack gap='3'>
            <Heading as='h2'>{heading}</Heading>
            {canSendMessage && (
              <MessageSender
                listingId={listingId}
                recipient={recipient}
                defaultMessage={defaultConversationMessage}
                onSend={onMessageSend}
                checkCanSendMessage={checkCanSendMessage}
              />
            )}
            {hasConversations && <Button href={conversationPath}>View Your Conversation</Button>}
            <Button href={telesafeUrl} variant='secondary'>
              {hasTelesafe ? 'Call On Phone' : 'Call with TELESAFE™'}
            </Button>

            {showSuccessfulMessage && (
              <Modal
                showModal={showSuccessfulMessage}
                shouldCloseOnEsc={true}
                onCloseModal={() => setShowSuccessfulMessage(false)}
                height='auto'
                width='480px'
              >
                <MessageSendSuccessfulBox
                  avatar={avatar}
                  city={city}
                  conversationPath={conversationPath}
                  firstName={firstName}
                  similarListings={similarListings}
                  state={state}
                />
              </Modal>
            )}
          </Stack>
        </ContentBlock>
      )}
      {!isTabletAndUp && (
        <MobileContainer>
          {showMobileMessageSender && outOfCredits && (
            <Overlay>
              <ContentBlock p='4'>
                <CreditsBanner
                  credits={credits}
                  period={period}
                  isProvider
                  onClose={() => setShowMobileMessageSender(false)}
                />
              </ContentBlock>
            </Overlay>
          )}
          <Columns>
            {canSendMessage && (
              <Button onClick={() => setShowMobileMessageSender(true)} fluid>
                Message
              </Button>
            )}
            {!canSendMessage && (
              <Button as='a' href={conversationPath} fluid>
                Message
              </Button>
            )}
            {outOfCredits ? (
              <Button variant='secondary' fluid onClick={() => setShowMobileMessageSender(true)}>
                Call
              </Button>
            ) : (
              <Button as='a' href={telesafeUrl} variant='secondary' fluid>
                Call
              </Button>
            )}
            {showMobileMessageSender && !outOfCredits && (
              <Modal
                showModal={showMobileMessageSender}
                shouldCloseOnEsc={true}
                onCloseModal={() => setShowMobileMessageSender(false)}
                height='auto'
                width='85vw'
              >
                {!showSuccessfulMessage && (
                  <>
                    <Text as='h2' mt={0} variant='heading' fontWeight='bold'>
                      {heading}
                    </Text>
                    <MessageSender
                      listingId={listingId}
                      recipient={recipient}
                      defaultMessage={defaultConversationMessage}
                      onSend={onMessageSend}
                      checkCanSendMessage={checkCanSendMessage}
                    />
                  </>
                )}
                {showSuccessfulMessage && (
                  <MessageSendSuccessfulBox
                    avatar={avatar}
                    city={city}
                    conversationPath={conversationPath}
                    firstName={firstName}
                    similarListings={similarListings}
                    state={state}
                  />
                )}
              </Modal>
            )}
          </Columns>
        </MobileContainer>
      )}
    </>
  )
}

const MobileContainer = styled(Box).attrs({ as: 'span' })`
  box-sizing: border-box;
  position: fixed;
  bottom: 0px;
  left: 0px;
  width: 100vw;
  z-index: 1000;
`

const Columns = styled.span`
  ${css({
    marginLeft: -4,
    backgroundColor: 'gray.1',
    p: 4,
  })}

  display: flex;
  flex-direction: row;
  align-items: center;

  > * {
    flex-basis: 0;
    flex-shrink: 0;
    flex-grow: 1;

    ${css({
      ml: '4',
    })}
  }
`

export default Messenger
